'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import {
  useNameGenerator,
  colorOptions,
  personalityOptions,
  ambitionOptions,
  beliefOptions,
  seasonOptions,
  historicalFigureOptions,
  favouriteColorOptions,
  animalOptions,
  kindOptions,
  cultureOptions,
  type PupNameItem
} from '@/features/nameGenerator';
import type { BreedWithMedia } from '@/features/breeds';
import { Button, SearchFilterSelect, ShareLinks } from '@paction-2.0/ui';
import { ChipField } from '@paction-2.0/ui/client';

import styles from './dogNameGeneratorPage.module.scss';

type NameGeneratorClientProps = {
  pupNames: PupNameItem[];
  breeds: BreedWithMedia[];
};

function MaleIcon({ color = '#000' }: { color?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      viewBox="-5.0 -10.0 110.0 100.0"
    >
      <path
        d="m48.609 90.734c0.023437 0.019531 0.054687 0.046875 0.078125 0.066406 0.015625 0.015625 0.03125 0.03125 0.050781 0.046875 0.007813 0.007813 0.019531 0.011719 0.027344 0.019532h0.003906c0.023438 0.019531 0.046875 0.035156 0.070313 0.054687 0.011718 0.007813 0.023437 0.019531 0.035156 0.03125 0.015625 0.007813 0.03125 0.019531 0.039062 0.027344l0.003907 0.003906c0.027343 0.015625 0.054687 0.03125 0.082031 0.046875h0.003906c0.015625 0.007812 0.035156 0.023438 0.054688 0.035156 0.007812 0.003906 0.011718 0.007813 0.019531 0.011719 0.03125 0.015625 0.058594 0.027344 0.089844 0.042969h0.003906c0.015625 0.007812 0.039063 0.019531 0.058594 0.027344 0.003906 0 0.007812 0.003906 0.011719 0.003906 0.003906 0 0.007812 0.003906 0.007812 0.003906 0.027344 0.011719 0.058594 0.019531 0.089844 0.03125 0.003906 0 0.003906 0 0.007812 0.003906 0.015625 0.003906 0.03125 0.011719 0.046875 0.019532 0.011719 0.003906 0.019531 0.003906 0.03125 0.007812h0.003907c0.03125 0.007812 0.0625 0.015625 0.097656 0.023438 0.023437 0.003906 0.042968 0.011718 0.046875 0.015624 0.007812 0 0.015625 0.003907 0.023437 0.003907 0.003906 0 0.011719 0.003906 0.015625 0.003906 0.035157 0.007813 0.074219 0.011719 0.11328 0.015625 0.019532 0.003906 0.039063 0.007812 0.0625 0.011719 0.007813 0 0.015626 0 0.023438 0.003906 0.058594 0.003906 0.11719 0.007813 0.17578 0.007813 0.058594 0 0.11719-0.003907 0.17188-0.007813 0.027344-0.003906 0.066406-0.011719 0.089844-0.015625 0.035156-0.003906 0.074219-0.007812 0.10938-0.015625 0.003906 0 0.011719-0.003906 0.015625-0.003906s0.007812 0 0.011719-0.003907c0.019531-0.003906 0.039062-0.011718 0.058593-0.015624 0.03125-0.007813 0.0625-0.011719 0.089844-0.023438 0.011719-0.003906 0.023438-0.007812 0.035156-0.011719 0.015626-0.007812 0.03125-0.015625 0.046876-0.019531 0 0 0.003906 0 0.003906-0.003906 0.027344-0.011719 0.058594-0.019532 0.089844-0.03125 0.003906 0 0.007812-0.003906 0.015624-0.003906 0.023438-0.011719 0.046876-0.023438 0.066407-0.03125 0.027343-0.011719 0.058593-0.027344 0.085937-0.039063 0 0 0.003906 0 0.003906-0.003906 0.003907-0.003907 0.011719-0.007813 0.019532-0.007813 0.019531-0.011718 0.042968-0.027344 0.058594-0.035156 0.027343-0.015625 0.054687-0.03125 0.082031-0.050781 0.003906-0.003907 0.007812-0.003907 0.007812-0.007813 0.007813-0.003906 0.011719-0.007812 0.019531-0.011718 0.019532-0.011719 0.035157-0.027344 0.050782-0.039063 0.023437-0.015625 0.046875-0.03125 0.070312-0.050781 0.011719-0.007813 0.023438-0.015625 0.035156-0.027344 0.015626-0.015625 0.03125-0.03125 0.046876-0.042969l0.003906-0.003906c0.019531-0.015625 0.050781-0.042969 0.070312-0.0625l13.445-12.773c0.78125-0.74219 0.8125-1.9805 0.070312-2.7617-0.74219-0.78125-1.9805-0.8125-2.7617-0.070313l-6.8789 6.5352c-0.56641 0.53906-1.3984 0.6875-2.1133 0.37891-0.71875-0.30859-1.1836-1.0117-1.1836-1.793v-26.609c0-0.95312 0.69141-1.7695 1.6289-1.9258 10.25-1.7109 18.09-10.645 18.09-21.375 0-11.949-9.7227-21.672-21.672-21.672-3.2227 0-6.2852 0.70703-9.0391 1.9766-0.98047 0.44922-2.1406 0.023438-2.5898-0.95703s-0.023438-2.1406 0.95703-2.5898c3.25-1.4961 6.8672-2.3359 10.672-2.3359 14.105 0 25.578 11.473 25.578 25.578 0 12.09-8.4297 22.242-19.719 24.898v20.461l3.582-3.4023c2.3438-2.2266 6.0547-2.1328 8.2812 0.21094 2.2266 2.3438 2.1328 6.0547-0.21094 8.2812l-13.477 12.801c-0.039062 0.039062-0.082031 0.074218-0.125 0.10938-0.046874 0.042969-0.09375 0.085937-0.14453 0.12891-0.003906 0.003906-0.003906 0.003906-0.007812 0.007813-0.03125 0.027343-0.0625 0.050781-0.097657 0.078124 0 0-0.003906 0.003907-0.003906 0.003907-0.066406 0.054687-0.13672 0.10547-0.20703 0.15625-0.050781 0.039062-0.10547 0.078125-0.15625 0.11719-0.03125 0.019532-0.0625 0.039063-0.09375 0.0625-0.078125 0.050782-0.16016 0.097656-0.23828 0.14453-0.054688 0.03125-0.10547 0.066406-0.16016 0.09375h-0.003907c-0.023437 0.011718-0.066406 0.035156-0.09375 0.046874-0.082031 0.042969-0.16406 0.082032-0.24609 0.11719-0.058594 0.027344-0.11328 0.054688-0.16797 0.078125-0.027344 0.011719-0.0625 0.027344-0.097657 0.039063-0.082031 0.035156-0.16797 0.0625-0.25391 0.09375-0.066406 0.023437-0.12891 0.046874-0.21094 0.074218-0.019531 0.007813-0.046875 0.011719-0.066407 0.019532-0.089843 0.027343-0.17969 0.046874-0.26953 0.070312h-0.003906c-0.074219 0.019531-0.14844 0.039062-0.22656 0.054688-0.007812 0-0.015624 0.003906-0.027343 0.003906-0.011719 0.003906-0.027344 0.003906-0.039063 0.007812-0.10156 0.019532-0.19922 0.03125-0.30078 0.046875-0.066406 0.007813-0.13672 0.019531-0.20312 0.027344-0.011719 0-0.027344 0.003906-0.039062 0.003906h-0.003907c-0.17969 0.015625-0.35938 0.027344-0.54297 0.027344-0.18359 0-0.36328-0.007813-0.54687-0.027344h-0.015626c-0.007812 0-0.019531 0-0.027343-0.003906-0.066407-0.007813-0.12891-0.019531-0.19531-0.027344h-0.003906c-0.10547-0.015625-0.21094-0.027343-0.30469-0.046875-0.011718-0.003906-0.023906-0.003906-0.039062-0.007812s-0.023438-0.003906-0.035156-0.007813c-0.070313-0.015625-0.14062-0.035156-0.21094-0.050781h-0.003906c-0.089844-0.023438-0.17969-0.042969-0.26953-0.070312-0.027344-0.007813-0.054688-0.015626-0.078125-0.023438-0.066406-0.019531-0.13281-0.046875-0.19922-0.070312-0.085937-0.03125-0.17188-0.058594-0.25-0.09375-0.027344-0.011719-0.0625-0.023438-0.085937-0.035157-0.003907 0-0.003907-0.003906-0.007813-0.003906-0.058594-0.023437-0.11328-0.054687-0.17188-0.082031-0.085938-0.039063-0.17188-0.082032-0.25-0.12109-0.03125-0.015625-0.074219-0.035156-0.097656-0.050781-0.054688-0.03125-0.10938-0.066407-0.16016-0.09375-0.085938-0.050781-0.16797-0.10156-0.24609-0.15234-0.03125-0.019531-0.058594-0.039063-0.09375-0.058594-0.050782-0.035156-0.10156-0.074219-0.15625-0.11328-0.070313-0.050781-0.14062-0.10156-0.20703-0.15625-0.035156-0.027344-0.070312-0.054688-0.10547-0.082031 0 0-0.003906-0.003907-0.003906-0.003907-0.050782-0.042968-0.097657-0.085937-0.14453-0.12891-0.039062-0.035156-0.078125-0.066406-0.11719-0.10156l-0.003907-0.003906-13.477-12.801c-2.3438-2.2266-2.4414-5.9375-0.21094-8.2812 2.2266-2.3438 5.9336-2.4414 8.2812-0.21094l3.582 3.4023v-20.461c-11.289-2.6562-19.719-12.809-19.719-24.898 0-5.6328 1.8281-10.844 4.9258-15.074 0.63672-0.87109 1.8594-1.0586 2.7305-0.42188 0.87109 0.63672 1.0586 1.8594 0.42188 2.7305-2.6211 3.582-4.1719 8-4.1719 12.77 0 10.73 7.8398 19.664 18.09 21.375 0.94141 0.15625 1.6289 0.97266 1.6289 1.9258v26.609c0 0.78125-0.46484 1.4844-1.1836 1.793-0.71875 0.30859-1.5508 0.16016-2.1133-0.37891l-6.8789-6.5352c-0.78125-0.74219-2.0195-0.71094-2.7617 0.070313-0.74219 0.78125-0.71094 2.0156 0.070312 2.7617 0 0 12.805 12.164 13.445 12.773zm-16.395-60.391c0-9.7969 7.9688-17.766 17.766-17.766s17.766 7.9688 17.766 17.766-7.9688 17.766-17.766 17.766-17.766-7.9688-17.766-17.766zm3.9062 0c0 7.6445 6.2188 13.863 13.863 13.863s13.863-6.2188 13.863-13.863-6.2188-13.863-13.863-13.863-13.863 6.2188-13.863 13.863z"
        fillRule="evenodd"
        fill={color}
      />
    </svg>
  );
}

function FemaleIcon({ color = '#000' }: { color?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      viewBox="-5.0 -10.0 110.0 100.0"
    >
      <path
        d="m28.973 15.773c0.61328-0.88672 1.832-1.1055 2.7188-0.48828 0.88672 0.61328 1.1055 1.832 0.48828 2.7188-2.4375 3.5039-3.8672 7.7578-3.8672 12.34 0 10.73 7.8398 19.664 18.09 21.375 0.94141 0.15625 1.6289 0.97266 1.6289 1.9258v12.156c0 1.0781-0.875 1.9531-1.9531 1.9531h-9.5703c-1.0781 0-1.9531 0.875-1.9531 1.9531s0.875 1.9531 1.9531 1.9531h9.5703c1.0781 0 1.9531 0.875 1.9531 1.9531v15.738c0 1.0781 0.875 1.9531 1.9531 1.9531s1.9531-0.875 1.9531-1.9531v-15.738c0-1.0781 0.875-1.9531 1.9531-1.9531h9.5703c1.0781 0 1.9531-0.875 1.9531-1.9531s-0.875-1.9531-1.9531-1.9531h-9.5703c-1.0781 0-1.9531-0.875-1.9531-1.9531v-12.156c0-0.95312 0.69141-1.7695 1.6289-1.9258 10.25-1.7109 18.09-10.645 18.09-21.375 0-11.949-9.7227-21.672-21.672-21.672-3.5039 0-6.8164 0.83594-9.7461 2.3203-0.96094 0.48828-2.1367 0.10156-2.625-0.85938-0.48828-0.96094-0.10156-2.1367 0.85938-2.625 3.4609-1.75 7.3711-2.7383 11.512-2.7383 14.105 0 25.578 11.473 25.578 25.578 0 12.09-8.4297 22.242-19.719 24.898v8.6055h7.6172c3.2344 0 5.8594 2.6211 5.8594 5.8594 0 3.2344-2.6211 5.8594-5.8594 5.8594h-7.6172v13.785c0 3.2344-2.6211 5.8594-5.8594 5.8594-3.2344 0-5.8594-2.6211-5.8594-5.8594v-13.785h-7.6172c-3.2344 0-5.8594-2.6211-5.8594-5.8594 0-3.2344 2.6211-5.8594 5.8594-5.8594h7.6172v-8.6055c-11.289-2.6562-19.719-12.809-19.719-24.898 0-5.4102 1.6875-10.434 4.5625-14.57zm3.2461 14.57c0-9.7969 7.9688-17.766 17.766-17.766s17.766 7.9688 17.766 17.766-7.9688 17.766-17.766 17.766-17.766-7.9688-17.766-17.766zm3.9062 0c0 7.6445 6.2188 13.863 13.863 13.863s13.863-6.2188 13.863-13.863-6.2188-13.863-13.863-13.863-13.863 6.2188-13.863 13.863z"
        fillRule="evenodd"
        fill={color}
      />
    </svg>
  );
}

function SexSelector({
  value,
  onChange
}: {
  value: string;
  onChange: (sex: string) => void;
}) {
  const maleSelected = value === 'male';
  const femaleSelected = value === 'female';

  return (
    <div className={styles.sexSelector}>
      <div className={styles.sexSelectorLabel}>What sex is the dog?</div>
      <div className={styles.sexSelectorOptions}>
        <button
          type="button"
          className={styles.sexSelectorOption}
          style={
            maleSelected
              ? {
                  backgroundColor: 'var(--color-brand-tertiary-500)',
                  borderColor: 'var(--color-primary)'
                }
              : undefined
          }
          onClick={() => onChange('male')}
        >
          <div className={styles.sexSelectorIcon}>
            <MaleIcon color={maleSelected ? '#fff' : '#000'} />
          </div>
          <span
            className={styles.sexSelectorOptionLabel}
            style={
              maleSelected ? { color: 'var(--color-base-white)' } : undefined
            }
          >
            Male
          </span>
        </button>
        <button
          type="button"
          className={styles.sexSelectorOption}
          style={
            femaleSelected
              ? {
                  backgroundColor: 'var(--color-brand-tertiary-500)',
                  borderColor: 'var(--color-primary)'
                }
              : undefined
          }
          onClick={() => onChange('female')}
        >
          <div className={styles.sexSelectorIcon}>
            <FemaleIcon color={femaleSelected ? '#fff' : '#000'} />
          </div>
          <span
            className={styles.sexSelectorOptionLabel}
            style={
              femaleSelected ? { color: 'var(--color-base-white)' } : undefined
            }
          >
            Female
          </span>
        </button>
      </div>
    </div>
  );
}

const FONT_CLASSES = [
  styles.font1,
  styles.font2,
  styles.font3,
  styles.font4,
  styles.font5,
  styles.font6,
  styles.font7,
  styles.font8,
  styles.font9
];

function getRandomFontClass(): string {
  const fontClass =
    FONT_CLASSES[Math.floor(Math.random() * FONT_CLASSES.length)];
  return fontClass || '';
}

function NameResultsDisplay({ names }: { names: string[] }) {
  const fontMappingRef = useRef<Record<string, string>>({});

  useEffect(() => {
    names.forEach((name) => {
      if (!fontMappingRef.current[name]) {
        fontMappingRef.current[name] = getRandomFontClass();
      }
    });
  }, [names]);

  return (
    <div className={styles.nameResultsDisplay}>
      <div className={styles.nameResultsValues}>
        {names.map((name, index) => (
          <div
            key={index}
            className={`${styles.nameResultsValue} ${fontMappingRef.current[name] ?? styles.font1}`}
          >
            <div className={styles.nameResultsHeading}>{name}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NameResultsPlaceholder() {
  return (
    <div className={styles.nameResultsPlaceholder}>
      <div className={styles.nameResultsPlaceholderIcon}>
        <img
          className={styles.nameResultsPlaceholderImg}
          alt="name-generator"
          src="/images/pup-name-generator.webp"
        />
      </div>
    </div>
  );
}

export function NameGeneratorClient({
  pupNames,
  breeds
}: NameGeneratorClientProps) {
  const [selectedBreed, setSelectedBreed] = useState<string | undefined>(
    undefined
  );

  // Select Affenpinscher by default
  useEffect(() => {
    if (breeds.length > 0 && !selectedBreed) {
      const affenpinscher = breeds.find(
        (b) => b.name.toLowerCase() === 'affenpinscher'
      );
      if (affenpinscher) {
        setSelectedBreed(affenpinscher.slug);
      }
    }
  }, [breeds, selectedBreed]);

  const {
    sex,
    answers,
    nameSuggestions,
    isLoading,
    showInputs,
    handleSexChange,
    handleAnswerChange,
    generateNames,
    clearResults,
    startGenerator,
    inputsRef,
    resultsRef
  } = useNameGenerator(pupNames);

  const breedOptions = useMemo(
    () =>
      breeds.map((breed) => ({
        label: breed.name,
        value: breed.slug,
        meta: { isTargeting: breed.isTargeting }
      })),
    [breeds]
  );

  const handleBreedSelect = (value: string | undefined) => {
    setSelectedBreed(value);
  };

  const handleClearAll = () => {
    setSelectedBreed(undefined);
    clearResults();
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.querySelector('link[data-name-generator-fonts]')) return;

    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    preconnect1.setAttribute('data-name-generator-fonts', '');
    document.head.appendChild(preconnect1);

    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = '';
    preconnect2.setAttribute('data-name-generator-fonts', '');
    document.head.appendChild(preconnect2);

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.media = 'print';
    link.setAttribute('data-name-generator-fonts', '');
    link.id = 'name-generator-fonts';
    link.href =
      'https://fonts.googleapis.com/css2?family=Rowdies:wght@400&family=Concert+One&family=Chivo:wght@700&family=Patua+One&family=Francois+One&family=Shantell+Sans:wght@500&family=Kosugi+Maru&family=Viga&family=Itim&display=swap';
    link.onload = () => {
      link.media = 'all';
    };

    const append = () => document.head.appendChild(link);
    if ('requestIdleCallback' in window) {
      (window as Window).requestIdleCallback(append, { timeout: 1500 });
    } else {
      setTimeout(append, 0);
    }
  }, []);

  const canStart = selectedBreed !== undefined && sex !== '';

  return (
    <>
      {/* Intro Section */}
      <section className={styles.intro}>
        <h2 className={styles.introTitle}>
          <div className={styles.introTitleBold}>
            How to Choose the Perfect Name for Your Dog
          </div>
        </h2>
        <div className={styles.introText}>
          <p>
            Selecting the perfect name for your new pup is an exciting yet
            significant task. Just like with baby names, there&apos;s an
            obsession with finding the most unique and fitting name, so much so
            that an entire industry has sprung up around it—yes, Baby Naming
            Consultants are now a thing!
          </p>
          <p>
            To make this process easier and more enjoyable, Paction offers a
            free tool that generates a list of ideal potential names for your
            new furry friend. Naming your pup is one of the most thrilling parts
            of bringing a new member into your family, but it also comes with
            the responsibility of choosing a name that truly fits their role in
            your life. Since this name will be part of your dog&apos;s identity
            for years to come, it&apos;s worth taking the time to explore all
            the possibilities. With Paction&apos;s tool, you can lighten the
            creative burden and discover a thoughtful and original name that
            perfectly suits your new canine companion.
          </p>
        </div>
      </section>

      {/* Results ref for scrolling */}
      <div ref={resultsRef} />

      {/* Results Section */}
      <section className={styles.results}>
        <h2 className={styles.resultsTitle}>
          Generate the perfect name for your new pup
        </h2>
        <div className={styles.resultsContainer}>
          <div className={styles.resultsInputs}>
            <div className={styles.breedSelectorWrap}>
              <div id="breed-label" className={styles.breedLabel}>
                Select your ideal breed
              </div>
              <SearchFilterSelect
                variant="legacy"
                options={breedOptions}
                value={selectedBreed}
                placeholder="Select a breed"
                ariaLabelledby="breed-label"
                allowClear
                onSelect={handleBreedSelect}
              />
            </div>
            <SexSelector value={sex} onChange={handleSexChange} />
            <div className={styles.resultsBtnContainer}>
              {nameSuggestions.length > 0 ? (
                <Button
                  onClick={handleClearAll}
                  className={styles.resultsBtnContainerButton}
                >
                  Clear
                </Button>
              ) : (
                <Button
                  onClick={startGenerator}
                  disabled={!canStart}
                  className={styles.resultsBtnContainerButton}
                >
                  Get Started
                </Button>
              )}
            </div>
          </div>
          <div className={styles.resultsResultsArea}>
            {isLoading ? (
              <div className={styles.loading}>
                <div className={styles.spinner} />
                Generating...
              </div>
            ) : nameSuggestions.length > 0 ? (
              <NameResultsDisplay names={nameSuggestions} />
            ) : (
              <NameResultsPlaceholder />
            )}
          </div>
        </div>
        <div className={styles.resultsShare}>
          <ShareLinks link="https://paction.ca/dog-name-generator" onlyCopy />
        </div>
      </section>

      {/* Inputs ref for scrolling */}
      <div ref={inputsRef} />

      {/* Inputs Section */}
      {showInputs && (
        <section className={styles.inputs}>
          <div className={styles.inputsGroup}>
            <h3 className={styles.inputsTitle}>Tell us more about your pup</h3>
            <ChipField
              label="What colour is your puppy?"
              options={colorOptions}
              selectedOptions={answers.color}
              onChange={(value) => handleAnswerChange('color', value as string)}
            />
            <ChipField
              label="What is the dog's personality? Or, how do you imagine your puppy's personality?"
              options={personalityOptions}
              selectedOptions={answers.personality}
              onChange={(value) =>
                handleAnswerChange('personality', value as string[])
              }
              multiSelect
            />
          </div>

          <div className={styles.inputsGroup}>
            <h3 className={styles.inputsTitle}>
              Tell us a bit about you as the prospective pup parent
            </h3>
            <ChipField
              label="Which activities would you choose to do if you knew that you couldn't fail?"
              options={ambitionOptions}
              selectedOptions={answers.ambition}
              onChange={(value) =>
                handleAnswerChange('ambition', value as string[])
              }
              multiSelect
            />
            <ChipField
              label="Select a belief that you most closely identify with which others might find unconventional."
              options={beliefOptions}
              selectedOptions={answers.belief}
              onChange={(value) =>
                handleAnswerChange('belief', value as string)
              }
            />
            <ChipField
              label="What is your favourite season?"
              options={seasonOptions}
              selectedOptions={answers.season}
              onChange={(value) =>
                handleAnswerChange('season', value as string)
              }
            />
            <ChipField
              label="If you could live a day in the life of a historical figure, who would it be and why?"
              options={historicalFigureOptions}
              selectedOptions={answers.historicalFigure}
              onChange={(value) =>
                handleAnswerChange('historicalFigure', value as string)
              }
            />
            <ChipField
              label="What are your favourite colours?"
              options={favouriteColorOptions}
              selectedOptions={answers.favouriteColor}
              onChange={(value) =>
                handleAnswerChange('favouriteColor', value as string[])
              }
              multiSelect
            />
            <ChipField
              label="Which exotic animals do you prefer?"
              options={animalOptions}
              selectedOptions={answers.animal}
              onChange={(value) =>
                handleAnswerChange('animal', value as string[])
              }
              multiSelect
            />
          </div>

          <div className={styles.inputsGroup}>
            <h3 className={styles.inputsTitle}>
              Tell us about your name style preferences
            </h3>
            <ChipField
              label="What kind of names do you gravitate to?"
              options={kindOptions}
              selectedOptions={answers.kind}
              onChange={(value) =>
                handleAnswerChange('kind', value as string[])
              }
              multiSelect
            />
            <ChipField
              label="Do you have a preference for names from a particular culture?"
              options={cultureOptions}
              selectedOptions={answers.culture}
              onChange={(value) =>
                handleAnswerChange('culture', value as string)
              }
            />
          </div>

          <div className={styles.inputsButtonContainer}>
            <Button onClick={generateNames} className={styles.generateButton}>
              Generate Names
            </Button>
          </div>
        </section>
      )}
    </>
  );
}
